import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BotAction } from '../types';
import { DataTable, Cell, Empty } from '../table';

/**
 * The calls this action panel makes.
 *
 * Injected for the same reason the editor's is: PSM's console addresses a client's
 * actions through /admin/tenants/{id}, a client's own portal through routes the
 * server scopes for them. One panel, two addresses.
 */
export interface ActionStudioApi {
  save: (action: {
    actionKey: string; name?: string; description?: string; method?: string; url: string;
    headers?: Record<string, string>; authType?: string | null; authHeader?: string | null;
    secret?: string;
  }) => Promise<unknown>;
  remove: (id: number) => Promise<unknown>;
}

/**
 * The named calls a flow may make.
 *
 * A flow references one of these by key and never sees the URL or the credential —
 * which is not tidiness but the entire SSRF answer: a flow that cannot express a URL
 * cannot be pointed at an internal address.
 */
export function ActionsPanel({ actions, api, readOnly = false }: {
  actions: BotAction[];
  api: ActionStudioApi;
  readOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Retiring used to be fire-and-forget, and the failure was silent in the worst
  // possible way: a live bot kept running and started taking its "your system is
  // down" branch on every conversation. The server refuses that now; this is the
  // half that puts the refusal in front of the person who clicked.
  const retire = async (id: number) => {
    setError(null);
    setNote(null);
    try {
      const result = await api.remove(id) as { usedBy?: string[] } | null;
      if (result?.usedBy?.length) {
        setNote(`Retired. These drafts call it and will stop at that step: ${result.usedBy.join(', ')}.`);
      }
      await queryClient.invalidateQueries({ queryKey: ['bot-actions'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not retire that action.');
    }
  };

  const [form, setForm] = useState({
    actionKey: '', name: '', method: 'POST', url: '', authType: 'none', authHeader: '', secret: '',
  });

  const save = useMutation({
    mutationFn: () => api.save({
      actionKey: form.actionKey.trim(),
      name: form.name.trim() || form.actionKey.trim(),
      method: form.method,
      url: form.url.trim(),
      authType: form.authType === 'none' ? null : form.authType,
      authHeader: form.authHeader.trim() || null,
      secret: form.secret || undefined,
    }),
    onSuccess: () => {
      setError(null);
      setAdding(false);
      setForm({ actionKey: '', name: '', method: 'POST', url: '', authType: 'none', authHeader: '', secret: '' });
      void queryClient.invalidateQueries({ queryKey: ['bot-actions'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save that action.'),
  });

  const field = 'mt-1 w-full rounded-lg border border-(--color-line) bg-(--color-paper) px-3 py-2 text-body';

  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-lead font-semibold">Actions</h2>
        <button
          type="button"
          disabled={readOnly}
          onClick={() => setAdding((v) => !v)}
          className="ml-auto rounded-lg border border-(--color-line) px-3 py-1.5 text-body"
        >
          {adding ? 'Cancel' : 'Add an action'}
        </button>
      </div>

      <p className="mb-3 max-w-[90ch] text-small leading-relaxed text-(--color-muted)">
        A flow names one of these; it never carries a URL or a credential of its own.
        That is deliberate &mdash; a flow that cannot express an address cannot be
        pointed at an internal one, and a flow exported or pasted into a chat carries
        no secrets with it.
      </p>

      {error && (
        <p role="alert" className="mb-2 rounded-lg border border-(--color-bad) px-4 py-3 text-body text-(--color-bad)">
          {error}
        </p>
      )}

      {note && (
        <p className="mb-2 rounded-lg border border-(--color-warn) bg-(--color-warn-wash) px-4 py-3 text-body text-(--color-ink-soft)">
          {note}
        </p>
      )}

      {adding && (
        <form
          onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
          className="mb-3 grid gap-3 rounded-lg border border-(--color-line) bg-(--color-surface) p-4 sm:grid-cols-2"
        >
          <label className="block">
            <span className="text-small font-medium text-(--color-muted)">Key flows use</span>
            <input value={form.actionKey} onChange={(e) => setForm({ ...form, actionKey: e.target.value })} className={field} placeholder="crm_lookup" />
          </label>
          <label className="block">
            <span className="text-small font-medium text-(--color-muted)">Name</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} placeholder="Look up a customer" />
          </label>
          <label className="block">
            <span className="text-small font-medium text-(--color-muted)">Method</span>
            <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className={field}>
              {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-small font-medium text-(--color-muted)">URL (https only)</span>
            <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={field} placeholder="https://crm.example.com/customers" />
          </label>
          <label className="block">
            <span className="text-small font-medium text-(--color-muted)">Authentication</span>
            <select value={form.authType} onChange={(e) => setForm({ ...form, authType: e.target.value })} className={field}>
              <option value="none">None</option>
              <option value="bearer">Bearer token</option>
              <option value="apikey">API key header</option>
              <option value="basic">Basic (user:password)</option>
            </select>
          </label>
          {form.authType === 'apikey' && (
            <label className="block">
              <span className="text-small font-medium text-(--color-muted)">Header name</span>
              <input value={form.authHeader} onChange={(e) => setForm({ ...form, authHeader: e.target.value })} className={field} placeholder="X-API-Key" />
            </label>
          )}
          {form.authType !== 'none' && (
            <label className="block sm:col-span-2">
              <span className="text-small font-medium text-(--color-muted)">Credential</span>
              <input
                type="password"
                value={form.secret}
                onChange={(e) => setForm({ ...form, secret: e.target.value })}
                className={field}
                placeholder="Stored encrypted; never shown again"
              />
            </label>
          )}
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={save.isPending || !form.actionKey.trim() || !form.url.trim()}
              className="rounded-lg bg-(--color-brand) px-4 py-2 text-body font-medium text-white disabled:opacity-40"
            >
              {save.isPending ? 'Saving…' : 'Save action'}
            </button>
          </div>
        </form>
      )}

      {actions.length === 0 ? (
        <Empty>No actions yet. A flow needs one before it can call anything.</Empty>
      ) : (
        <>
        {/* Same reason as the flows list: Retire is the last column of a five-column
            table, so on a phone it sits off the right inside the scroller where a
            layout check does not look. Cards below sm. */}
        <ul className="flex flex-col gap-2 sm:hidden">
          {actions.map((action) => {
            const config = safeConfig(action.config);
            return (
              <li key={action.id} className="rounded-lg border border-(--color-line) bg-(--color-surface) p-4">
                <p className="font-mono text-small text-(--color-muted)">{action.actionKey}</p>
                <p className="text-lead font-medium">{action.name}</p>
                <p className="mt-1.5 font-mono text-small break-all text-(--color-muted)">
                  {config.method} {config.url}
                </p>
                <p className="mt-1.5 text-small text-(--color-muted)">
                  {action.hasSecret ? `${action.authType} · credential stored` : 'no credential'}
                </p>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => void retire(action.id)}
                    className="mt-3 w-full rounded-lg border border-(--color-line) py-2 text-body text-(--color-muted)"
                  >
                    Retire
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        <div className="hidden sm:block">
        <DataTable head={['Key', 'Name', 'Call', 'Credential', '']}>
          {actions.map((action) => {
            const config = safeConfig(action.config);
            return (
              <tr key={action.id} className="border-t border-(--color-line-dim)">
                <Cell mono nowrap>{action.actionKey}</Cell>
                <Cell>{action.name}</Cell>
                <Cell mono>
                  {/* The URL is one long unbreakable token, so without this it claims
                      the width and squeezes every other column — the name wrapped to
                      three lines beside a half-empty address. */}
                  <span className="break-all">{config.method} {config.url}</span>
                </Cell>
                <Cell>{action.hasSecret ? `${action.authType} · stored` : 'none'}</Cell>
                <Cell>
                  <button
                    type="button"
                    onClick={() => void retire(action.id)}
                    className="rounded-lg border border-(--color-line) px-3 py-1.5 text-small text-(--color-muted)"
                  >
                    Retire
                  </button>
                </Cell>
              </tr>
            );
          })}
        </DataTable>
        </div>
        </>
      )}
    </section>
  );
}

function safeConfig(json: string): { method?: string; url?: string } {
  try {
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as { method?: string; url?: string }) : {};
  } catch {
    return {};
  }
}
